/**
 * Super-admin live session viewer + consent-gated assist.
 *
 * Watches a live session read-only over an invisible `admin:observe` channel (the
 * observer is never added to presence, so talent/producer don't see it). Watching is
 * silent and unilateral. TAKING CONTROL is not: the admin requests it, and only once
 * the producer Allows does the server accept the admin's control actions. While
 * granted, the admin drives the FULL producer console (transport, cues, timers,
 * layout, display and the script) — genuinely working as the producer.
 */
import { useEffect, useMemo, useState } from 'react';
import type { HistoryEntry, InfoMessage, Project, SessionState } from '@goanna-im/shared';
import { getSocket } from '../lib/socket.js';
import { ScriptTextEditor } from './ScriptTextEditor.js';
import { ScriptMirror } from '../components/ScriptMirror.js';
import { ProducerControls, type ControlOps } from '../producer/ProducerRoute.js';
import { ProducerTimer } from '../producer/ProducerTimer.js';
import { RecordingToggle, ScriptHistory } from '../producer/ShowPanels.js';

type Control = 'observe' | 'requested' | 'granted';

export function AdminObserve({
  roomCode,
  owner,
  onClose,
}: {
  roomCode: string;
  owner: string;
  onClose: () => void;
}) {
  const socket = useMemo(() => getSocket(), []);
  const [session, setSession] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [control, setControl] = useState<Control>('observe');

  useEffect(() => {
    let active = true;
    const observe = () =>
      socket.emit('admin:observe', { roomCode }, (result) => {
        if (!active) return;
        if (result.ok) {
          setSession(result.session);
          setError(null);
        } else {
          setError(result.error);
        }
      });

    const patch = (p: Partial<SessionState>) => setSession((s) => (s ? { ...s, ...p } : s));
    const onScript = (project: Project) => patch({ project });
    const onScroll = (scroll: SessionState['scroll']) => patch({ scroll });
    const onCue = (message: InfoMessage) => patch({ message });
    const onCueClear = () => patch({ message: null });
    const onPresence = (presence: SessionState['presence']) => patch({ presence });
    const onSettings = (settings: SessionState['settings']) => patch({ settings });
    const onLayout = (layout: SessionState['layout']) => patch({ layout });
    const onTimers = (timers: SessionState['timers']) => patch({ timers });
    const onRecord = ({ on }: { on: boolean }) => patch({ recording: on });
    // The producer's Allow/Deny/Revoke lands here.
    const onAssistState = ({ granted }: { granted: boolean; adminName?: string }) =>
      setControl(granted ? 'granted' : 'observe');

    observe();
    socket.on('script:updated', onScript);
    socket.on('scroll:state', onScroll);
    socket.on('cue:shown', onCue);
    socket.on('cue:cleared', onCueClear);
    socket.on('presence:updated', onPresence);
    socket.on('settings:updated', onSettings);
    socket.on('layout:updated', onLayout);
    socket.on('timers:updated', onTimers);
    socket.on('record:state', onRecord);
    socket.on('assist:state', onAssistState);
    socket.on('connect', observe); // re-observe after a reconnect

    return () => {
      active = false;
      socket.emit('admin:leave', { roomCode }); // server releases any grant we hold
      socket.off('script:updated', onScript);
      socket.off('scroll:state', onScroll);
      socket.off('cue:shown', onCue);
      socket.off('cue:cleared', onCueClear);
      socket.off('presence:updated', onPresence);
      socket.off('settings:updated', onSettings);
      socket.off('layout:updated', onLayout);
      socket.off('timers:updated', onTimers);
      socket.off('record:state', onRecord);
      socket.off('assist:state', onAssistState);
      socket.off('connect', observe);
    };
  }, [socket, roomCode]);

  const ops: ControlOps = useMemo(
    () => ({
      scroll: (c) => socket.emit('scroll:control', c),
      showCue: (m) => socket.emit('cue:show', m),
      clearCue: () => socket.emit('cue:clear'),
      timer: (c) => socket.emit('timer:control', c),
      setLayout: (p) => socket.emit('layout:set', p),
      updateSettings: (patch) => socket.emit('settings:update', patch),
    }),
    [socket],
  );

  const requestControl = () => {
    socket.emit('assist:request', { roomCode });
    setControl('requested');
  };
  const releaseControl = () => {
    socket.emit('assist:release', { roomCode });
    setControl('observe');
  };

  const presenters = session?.presence.filter((p) => p.role === 'presenter') ?? [];
  const producers = session?.presence.filter((p) => p.role === 'producer') ?? [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal card stack"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 820, width: '94vw', maxHeight: '90vh', overflow: 'auto' }}
      >
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>
            Live session <span className="roomcode">{roomCode}</span>{' '}
            <span className="muted" style={{ fontWeight: 400 }}>· {owner}</span>
          </h3>
          <button onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <p style={{ color: 'var(--urgent, #ef4444)' }}>{error}</p>}

        {!session && !error && <p className="muted">Connecting to the session…</p>}

        {session && (
          <>
            <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
              <span className="tag readout">
                {session.scroll.running ? '▶ scrolling' : '⏸ paused'} · {Math.round(session.scroll.speedPxPerSec)} px/s
              </span>
              <span className="muted">
                {presenters.length} presenter(s){presenters.length ? ` — ${presenters.map((p) => p.name || 'talent').join(', ')}` : ''} ·{' '}
                {producers.length} producer(s)
              </span>
            </div>

            {session.message && (
              <div className="card" style={{ borderColor: 'var(--accent-dim)', padding: '0.5rem 0.75rem' }}>
                <strong style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>{session.message.heading}</strong>
                <div>{session.message.body}</div>
              </div>
            )}

            {/* Control state banner */}
            {control === 'observe' && (
              <div className="row" style={{ justifyContent: 'space-between', gap: '0.5rem' }}>
                <span className="muted" style={{ fontSize: '0.85rem' }}>
                  You're observing silently. Request control to run this show — the producer must allow it.
                </span>
                <button className="primary" onClick={requestControl} disabled={producers.length === 0}>
                  Request control
                </button>
              </div>
            )}
            {control === 'requested' && (
              <div className="row" style={{ justifyContent: 'space-between', gap: '0.5rem' }}>
                <span>⏳ Waiting for the producer to allow control…</span>
                <button onClick={releaseControl}>Cancel</button>
              </div>
            )}
            {control === 'granted' && (
              <div
                className="row"
                style={{ justifyContent: 'space-between', gap: '0.5rem', borderColor: 'var(--accent)' }}
              >
                <span>✅ <strong>You have control</strong> — you're running this show as producer.</span>
                <button className="danger" onClick={releaseControl}>Hand control back</button>
              </div>
            )}

            {control === 'granted' ? (
              <>
                <ProducerTimer />
                <RecordingToggle
                  recording={session.recording}
                  onToggle={(on) => socket.emit('record:set', { on })}
                />
                <ProducerControls session={session} ops={ops} />
                <section className="card stack">
                  <h3 style={{ marginTop: 0 }}>Script</h3>
                  <ScriptTextEditor project={session.project} onChange={(p) => socket.emit('script:update', p)} />
                  <p className="muted" style={{ margin: 0, fontSize: '0.78rem' }}>
                    Edits appear live on the presenter. Hand control back when you're done.
                  </p>
                </section>
                <ScriptHistory
                  listHistory={(): Promise<HistoryEntry[]> =>
                    new Promise((resolve) => socket.emit('history:list', resolve))
                  }
                  restoreHistory={(at: number): Promise<boolean> =>
                    new Promise((resolve) => socket.emit('history:restore', { at }, resolve))
                  }
                />
              </>
            ) : (
              <ScriptMirror project={session.project} anchorBlockId={session.scroll.anchor.blockId} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
